import { z } from "zod";
import { AgentVersionId, type RouteAuth, Timestamp, UserId } from "../../common";
import { GatewayRequestHeaders } from "../../internal/v1alpha1/schemas";

/** Who may call a transport: a signed-in user for themselves, or the game server for a game. */
export const aiCallAuth: RouteAuth = {
  kind: "session",
  note: "A session sent as a cookie must come from Pax's own origin. The game server may instead call it for one agent of one game, with a service token carrying ai:call and the game call headers.",
};

export const AiPurpose = z
  .enum(["game_master", "player_agent", "grader", "client", "automod"])
  .meta({
    id: "AiPurpose",
    description:
      "What a call is for. It picks the call's accounting kind, which sets its rate and caps. `client` is a signed-in user's own call; `automod` is a moderation check Pax makes and pays for itself, reserving nothing; the others are an agent's in a game.",
    example: "game_master",
  });
export type AiPurpose = z.infer<typeof AiPurpose>;

export const ClientAllowanceSpentData = z
  .strictObject({
    allowanceRaw: z.string().regex(/^\d+$/).meta({
      description: "The daily share of free credits direct AI calls may spend.",
      example: "7500000000",
    }),
    resetsAt: Timestamp.meta({
      description: "When enough of the last day's spending rolls off to admit calls again.",
      example: "2026-10-10T09:14:00.000Z",
    }),
  })
  .meta({
    id: "ClientAllowanceSpentData",
    description: "The free allowance for direct AI calls, and when it frees up.",
  });
export type ClientAllowanceSpentData = z.infer<typeof ClientAllowanceSpentData>;

/**
 * A signed-in user's own call, refused because the last day's direct calls used up the free
 * allowance and the user holds no purchased credits. It comes back in the Product API's error
 * shape, like `STEP_UP_REQUIRED`, so Pax's clients can explain it.
 */
export const clientAllowanceSpent = {
  CLIENT_ALLOWANCE_SPENT: {
    status: 402,
    message:
      "The free allowance for direct AI calls is used up until data.resetsAt. Buying credits removes the limit.",
    data: ClientAllowanceSpentData,
  },
} as const;

export const GameCallPurpose = AiPurpose.exclude(["client", "automod"]);
export type GameCallPurpose = z.infer<typeof GameCallPurpose>;

export const AiPayer = z
  .strictObject({
    userId: UserId,
    shareWeight: z.number().int().positive().max(2_147_483_647).meta({
      description:
        "This payer's whole-number share of each charge, against the other payers' weights.",
      example: 1,
    }),
  })
  .meta({
    id: "AiPayer",
    description: "One player who pays for calls, and how large a share.",
    example: { userId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884", shareWeight: 1 },
  });
export type AiPayer = z.infer<typeof AiPayer>;

/**
 * Narrows one call's payers to some of the agent's payers: their user ids, separated by commas.
 * Without it, every payer of the agent pays.
 */
export const BENEFICIARIES_HEADER = "x-pax-beneficiaries";

/**
 * What the game server sends with each call it makes for an agent: the game and agent, the agent's
 * published version, what its calls are for, who pays, as user ids separated by commas that share
 * each charge evenly, and the round in play and latest committed revision when the call was made.
 */
export const GameCallHeaders = GatewayRequestHeaders.extend({
  "x-pax-agent-version-id": AgentVersionId,
  "x-pax-ai-purpose": GameCallPurpose,
  "x-pax-payers": z
    .string()
    .transform(payers => payers.split(","))
    .pipe(z.array(UserId).min(1)),
  "x-pax-game-round": z.coerce.number().int().positive(),
  "x-pax-game-head-revision": z.coerce.number().int().nonnegative(),
  /**
   * The most some payers may spend in the game on calls for this purpose, as `userId=rawCredits`
   * pairs separated by commas. A payer it doesn't name has no limit there.
   */
  "x-pax-payer-limits": z
    .string()
    .nullable()
    .transform(limits =>
      limits
        ? limits.split(",").map(pair => {
            const [userId = "", raw = ""] = pair.split("=");
            return { userId, limitRaw: raw };
          })
        : [],
    )
    .pipe(
      z.array(z.strictObject({ userId: UserId, limitRaw: z.string().regex(/^[1-9]\d{0,30}$/) })),
    ),
});
export type GameCallHeaders = z.infer<typeof GameCallHeaders>;
